import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaComment, FaTimes, FaPaperPlane, FaUser, FaEnvelope, FaPhone } from 'react-icons/fa';
import axios from 'axios';
import API_BASE_URL from '../config';
import { useLanguage } from '../contexts/LanguageContext';
import './ChatWidget.css';

// Строгая стильная иконка для чата
const ChatIcon = ({ isOpen }) => (
  <div className="chat-icon-wrapper">
    <svg viewBox="0 0 24 24" className="chat-icon-svg" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      <line x1="8" y1="10" x2="16" y2="10" />
      <line x1="8" y1="14" x2="12" y2="14" />
    </svg>
  </div>
);

const ChatWidget = () => {
  const { t } = useLanguage();
  const [showTooltip, setShowTooltip] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const tooltipTimeoutRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const messageRef = useRef(null);

  useEffect(() => {
    if (isOpen && messageRef.current) {
      messageRef.current.focus();
    }
  }, [isOpen]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  // Очистка таймера при размонтировании
  useEffect(() => {
    return () => {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Валидация
    if (!formData.message.trim()) {
      setError(t('chat.errorRequired'));
      return;
    }

    try {
      setSubmitting(true);
      
      const response = await axios.post(`${API_BASE_URL}/api/chat/send`, {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        message: formData.message.trim()
      });

      if (response.data.success) {
        setSuccess(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: ''
        });
        
        // Скрываем сообщение об успехе через 3 секунды
        setTimeout(() => {
          setSuccess(false);
          setIsOpen(false);
        }, 3000);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      let errorMessage = '';
      
      if (error.response) {
        // Сервер вернул ошибку
        errorMessage = error.response.data?.error || t('chat.errorSend');
      } else if (error.request) {
        // Запрос был отправлен, но ответа не получено
        errorMessage = t('chat.errorConnection') || 'Connection error. Please check your internet connection.';
      } else {
        // Ошибка при настройке запроса
        errorMessage = t('chat.errorSend');
      }
      
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Chat Trigger - полоска справа с подсказкой */}
      <div 
        className="chat-trigger-area"
        onMouseEnter={() => {
          // Очищаем таймер, если он есть
          if (tooltipTimeoutRef.current) {
            clearTimeout(tooltipTimeoutRef.current);
            tooltipTimeoutRef.current = null;
          }
          setShowTooltip(true);
        }}
        onMouseLeave={() => {
          // Добавляем задержку перед скрытием подсказки (увеличена до 1500мс)
          tooltipTimeoutRef.current = setTimeout(() => {
            setShowTooltip(false);
            tooltipTimeoutRef.current = null;
          }, 1500); // 1500мс задержка
        }}
        onClick={() => {
          setIsOpen(true);
          setShowTooltip(false);
          if (tooltipTimeoutRef.current) {
            clearTimeout(tooltipTimeoutRef.current);
            tooltipTimeoutRef.current = null;
          }
        }}
      >
        <AnimatePresence>
          {showTooltip && !isOpen && (
            <motion.div
              className="chat-tooltip"
              initial={{ opacity: 0, x: 30, scale: 0.8 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 30, scale: 0.8 }}
              transition={{ 
                type: "spring",
                stiffness: 300,
                damping: 25,
                duration: 0.4
              }}
              onClick={(e) => {
                e.stopPropagation();
                setIsOpen(true);
                setShowTooltip(false);
                if (tooltipTimeoutRef.current) {
                  clearTimeout(tooltipTimeoutRef.current);
                  tooltipTimeoutRef.current = null;
                }
              }}
              onMouseEnter={() => {
                // Отменяем скрытие при наведении на подсказку
                if (tooltipTimeoutRef.current) {
                  clearTimeout(tooltipTimeoutRef.current);
                  tooltipTimeoutRef.current = null;
                }
              }}
              onMouseLeave={() => {
                // Добавляем задержку при отведении от подсказки
                tooltipTimeoutRef.current = setTimeout(() => {
                  setShowTooltip(false);
                  tooltipTimeoutRef.current = null;
                }, 1500);
              }}
            >
              <ChatIcon isOpen={false} />
              <span>{t('chat.title')}</span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Chat Button - скрыта, используется только триггер справа */}
      {/* <button
        className="chat-widget-button"
        onClick={() => setIsOpen(!isOpen)}
      >
        <ChatIcon isOpen={isOpen} />
      </button> */}

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="chat-widget-window"
            initial={{ opacity: 0, x: 400, y: '-50%' }}
            animate={{ opacity: 1, x: 0, y: '-50%' }}
            exit={{ opacity: 0, x: 400, y: '-50%' }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
            <div className="chat-widget-header">
              <div className="chat-header-content">
                <FaComment />
                <h3>{t('chat.title')}</h3>
              </div>
              <button
                className="chat-close-btn"
                onClick={() => setIsOpen(false)}
              >
                <FaTimes />
              </button>
            </div>

            <div className="chat-widget-body">
              {success ? (
                <motion.div
                  className="chat-success"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                >
                  <div className="success-icon">✓</div>
                  <h4>{t('chat.successTitle')}</h4>
                  <p>{t('chat.successDesc')}</p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="chat-form">
                  {error && (
                    <motion.div
                      className="chat-error"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      {error}
                    </motion.div>
                  )}

                  <div className="chat-form-group">
                    <label htmlFor="chat-name">
                      <FaUser /> {t('chat.name')}
                    </label>
                    <input
                      type="text"
                      id="chat-name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder={t('chat.namePlaceholder')}
                    />
                  </div>

                  <div className="chat-form-group">
                    <label htmlFor="chat-email">
                      <FaEnvelope /> {t('chat.email')}
                    </label>
                    <input
                      type="email"
                      id="chat-email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder={t('chat.emailPlaceholder')}
                    />
                  </div>

                  <div className="chat-form-group">
                    <label htmlFor="chat-phone">
                      <FaPhone /> {t('chat.phone')}
                    </label>
                    <input
                      type="tel"
                      id="chat-phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder={t('chat.phonePlaceholder')}
                    />
                  </div>

                  <div className="chat-form-group">
                    <label htmlFor="chat-message">
                      {t('chat.message')} <span className="required">*</span>
                    </label>
                    <textarea
                      ref={messageRef}
                      id="chat-message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder={t('chat.messagePlaceholder')}
                      rows="4"
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    className="chat-submit-btn"
                    disabled={submitting || !formData.message.trim()}
                  >
                    {submitting ? (
                      <>
                        <span className="spinner-small"></span> {t('chat.submitting')}
                      </>
                    ) : (
                      <>
                        <FaPaperPlane /> {t('chat.submit')}
                      </>
                    )}
                  </button>

                  <p className="chat-form-note">
                    {t('chat.note')}
                  </p>
                </form>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatWidget;

